/**
 * skaile preset — scaffold, validate, export, and apply preset YAML files.
 *
 * Subcommands: init, validate, export, apply.
 * Delegates to @skaile/library preset module for parsing and validation.
 */

import { existsSync, readFileSync, writeFileSync } from "node:fs";
import * as readline from "node:readline/promises";
import { normalizeRepoUrl } from "@skaile/workspaces/asset-manager";
import {
  type LockFile,
  loadSkWorkspaceConfig,
  readLock,
  type SkWorkspaceConfig,
  WorkspaceYamlEditor,
} from "@skaile/workspaces/core";
import { getDefaultValue, isSecretPlaceholder } from "@skaile/workspaces/library";
import {
  type Placeholder,
  type PresetManifest,
  validatePreset,
} from "@skaile/workspaces/types/manifests";
import { Command } from "commander";
import path from "node:path";
import { stringify } from "yaml";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import {
  NESTED_PRESET_UNSUPPORTED,
  presetItemsToWorkspace,
  type WorkspaceItems,
} from "./preset-apply.ts";
import { reportInstalledSoftDeps } from "./project.ts";
import {
  buildPresetFromWorkspace,
  neededSources,
  redactSecrets,
  scanInlineSecrets,
  type SecretFinding,
  secretHeaderComment,
  sourcesWithSidecar,
  urlHasCredentials,
} from "./preset-export.ts";

/**
 * Creates the `skaile preset` command group.
 *
 * Scaffolds and validates preset YAML files used by the Asset Store v2.
 * Subcommands: `init [name]` (scaffold a `.preset.yaml` template) and
 * `validate <path>` (schema + cycle + nesting-depth check).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#preset
 */
export function makePresetCommand(): Command {
  const cmd = new Command("preset").description("Scaffold and validate preset YAML files");

  // ── preset init ─────────────────────────────────────────────────────────

  cmd
    .command("init [name]")
    .description("Scaffold a new .preset.yaml file")
    .option("--dir <path>", "Output directory", process.cwd())
    .action(async (name: string | undefined, opts: { dir: string }) => {
      const presetName = name ?? "my-preset";
      const filename = `${presetName}.preset.yaml`;
      const outPath = path.join(path.resolve(opts.dir), filename);

      if (existsSync(outPath)) {
        logErr(`File already exists: ${outPath}`);
        logInfo("Choose a different name or remove the existing file.");
        process.exit(1);
      }

      const template = generatePresetTemplate(presetName);
      writeFileSync(outPath, template, "utf-8");
      logOk(`Created ${S.brand(filename)}`);
      logInfo(`Path: ${outPath}`);
      logInfo("Edit the file to define your preset items, then validate with:");
      logInfo(`  skaile preset validate ${filename}`);
    });

  // ── preset validate ─────────────────────────────────────────────────────

  cmd
    .command("validate <path>")
    .description("Validate a preset YAML file (schema + cycles + nesting depth)")
    .action(async (filePath: string) => {
      await runPresetValidate(filePath);
    });

  // ── preset export ────────────────────────────────────────────────────────

  cmd
    .command("export [name]")
    .description("Snapshot the workspace's skaile.yaml into a shareable .preset.yaml")
    .option("--project-dir <path>", "Project directory to read skaile.yaml from", process.cwd())
    .option("--out <file>", "Explicit output file path")
    .option("--dir <path>", "Output directory (default: cwd)", process.cwd())
    .option("--redact", "Replace every detected inline secret with a placeholder")
    .option(
      "--allow-inline-secrets",
      "Exit 0 even when inline secrets remain unredacted (opt-in; unsafe for CI)",
    )
    .option("--force", "Overwrite the output file if it already exists")
    .action(async (name: string | undefined, opts: PresetExportOpts) => {
      await runPresetExport(name, opts);
    });

  // ── preset apply ─────────────────────────────────────────────────────────

  cmd
    .command("apply <path>")
    .description("Apply a .preset.yaml additively into the workspace's skaile.yaml")
    .option("--project-dir <path>", "Project directory holding skaile.yaml", process.cwd())
    .option(
      "--input <KEY=VAL>",
      "Provide a placeholder value (repeatable)",
      collectInput,
      [] as string[],
    )
    .option("--yes", "Skip interactive prompts; use defaults where available")
    .option("--no-deploy", "Skip the final `skaile install` step")
    .action(async (filePath: string, opts: PresetApplyOpts) => {
      await runPresetApply(filePath, opts);
    });

  return cmd;
}

// ── preset export implementation ─────────────────────────────────────────────

interface PresetExportOpts {
  projectDir: string;
  out?: string;
  dir: string;
  redact?: boolean;
  allowInlineSecrets?: boolean;
  force?: boolean;
}

/**
 * Full `skaile preset export` pipeline: read the project-own skaile.yaml, build
 * a preset, surface inline secrets loudly, optionally redact them, then write a
 * `.preset.yaml` — annotated with a warning header when secrets remain.
 */
async function runPresetExport(name: string | undefined, opts: PresetExportOpts): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const file = loadSkWorkspaceConfig(projectDir);
  if (!file) {
    logErr(`No skaile.yaml found in ${projectDir}`);
    logInfo("Run this from a project directory or pass --project-dir <path>.");
    process.exit(1);
  }

  const presetName = name ?? file.config.name ?? "my-preset";

  // Capture only the sources the deps resolve from (excludes the factory source).
  let lock: LockFile | null = null;
  try {
    lock = readLock(path.join(projectDir, "skaile.lock.yaml"));
  } catch {
    lock = null; // missing/legacy lock → fall back to all non-factory sources
  }
  const sources = neededSources(file.config, lock);
  if (lock === null && sources.length > 0) {
    logInfo("No lock file — exporting all non-factory sources conservatively.");
  }
  const strippedCreds = sources.filter((s) => urlHasCredentials(s.url)).length;
  if (strippedCreds > 0) {
    logWarn(
      `Stripped credentials from ${strippedCreds} source URL(s) — resolved via git auth on apply.`,
    );
  }
  // A captured source resolved through a machine-local sidecar overlay won't
  // reproduce on another machine until that sidecar manifest is itself shared.
  const sidecarSources = sourcesWithSidecar(sources);
  if (sidecarSources.length > 0) {
    logWarn(
      `${sidecarSources.length} captured source(s) rely on a MACHINE-LOCAL sidecar overlay ` +
        `(\`~/.skaile/store/manifests/*\`) — assets may not resolve on other machines or when ` +
        `this preset is shared, until the sidecar manifest is published: ${sidecarSources.join(", ")}`,
    );
  }
  let preset = buildPresetFromWorkspace(file.config, presetName, sources);

  if (preset.items.length === 0) {
    logErr("Nothing to export — skaile.yaml has no dependencies, connectors, or mcp_servers.");
    process.exit(1);
  }

  const result = validatePreset(preset);
  if (!result.ok) {
    logErr("Internal error: generated preset failed validation:");
    for (const err of result.errors) console.error(`  ${S.err("✗")} ${err.path}: ${err.message}`);
    process.exit(1);
  }

  // Secret safety: never a silent success. Report, optionally redact, then
  // re-scan the FINAL preset so `unresolved` is ground truth (a redaction that
  // failed to apply still surfaces here, keeping the warning honest).
  const findings = scanInlineSecrets(preset);
  reportFindings(findings);
  const toRedact = await resolveRedactions(findings, opts.redact === true);
  if (toRedact.length > 0) preset = redactSecrets(preset, toRedact).preset;
  const unresolved = scanInlineSecrets(preset);

  const header = secretHeaderComment(unresolved);
  const body = stringify(preset);
  const contents = header ? `${header}\n${body}` : body;

  if (opts.out && opts.dir !== process.cwd()) logWarn("--dir is ignored when --out is set.");
  const outPath = opts.out
    ? path.resolve(opts.out)
    : path.join(path.resolve(opts.dir), `${presetName}.preset.yaml`);
  if (existsSync(outPath) && !opts.force) {
    logErr(`File already exists: ${outPath}`);
    logInfo("Pass --force to overwrite, or choose a different --out/--dir.");
    process.exit(1);
  }

  try {
    writeFileSync(outPath, contents, "utf-8");
  } catch (e) {
    logErr(`Failed to write ${outPath}: ${(e as Error).message}`);
    process.exit(1);
  }
  logOk(`Exported ${S.brand(path.basename(outPath))}`);
  logInfo(`Path: ${outPath}`);

  // Fail closed: unresolved inline secrets must not read as a clean success — a
  // CI/script pipeline would otherwise publish the artifact with raw secrets.
  if (unresolved.length > 0) {
    console.error(S.err("This file is NOT safe to share."));
    if (!opts.allowInlineSecrets) {
      logErr(`${unresolved.length} inline secret(s) left unredacted.`);
      logInfo(
        "Re-run with --redact to placeholder them, or --allow-inline-secrets to accept the risk.",
      );
      process.exit(1);
    }
  }
}

/** Print a loud red summary of inline secrets to stderr (before any prompting). */
function reportFindings(findings: SecretFinding[]): void {
  if (findings.length === 0) return;
  console.error(S.err(`⚠️ ${findings.length} potential INLINE secret(s) found`));
  for (const f of findings) {
    console.error(`  ${S.err("•")} ${f.itemId}  ${S.dim(f.path)}  ${f.maskedValue}`);
  }
}

/**
 * Which findings to redact: all of them under `--redact`; otherwise prompt once
 * per finding when attached to a TTY; otherwise none (headless run leaves them
 * inline so the warning header is written).
 */
async function resolveRedactions(
  findings: SecretFinding[],
  redactAll: boolean,
): Promise<SecretFinding[]> {
  if (findings.length === 0) return [];
  if (redactAll) return findings;
  if (!process.stdin.isTTY) return [];

  const rl = readline.createInterface({ input: process.stdin, output: process.stderr });
  try {
    const chosen: SecretFinding[] = [];
    for (const f of findings) {
      const answer = await rl.question(`Redact ${f.itemId}.${f.path}? [y/N] `);
      if (/^y(es)?$/i.test(answer.trim())) chosen.push(f);
    }
    return chosen;
  } finally {
    rl.close();
  }
}

// ── preset apply implementation ──────────────────────────────────────────────

interface PresetApplyOpts {
  projectDir: string;
  input: string[];
  yes?: boolean;
  /** Commander sets this to `false` when `--no-deploy` is passed. */
  deploy: boolean;
}

/** Commander collector for repeatable `--input KEY=VAL` flags. */
function collectInput(value: string, acc: string[]): string[] {
  acc.push(value);
  return acc;
}

/**
 * Full `skaile preset apply <path>` pipeline: parse the preset, resolve
 * placeholders (secrets → `env:` refs, inputs → literals), merge the items
 * additively into the target `skaile.yaml` (comment-preserving), then deploy.
 */
async function runPresetApply(filePath: string, opts: PresetApplyOpts): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const file = loadSkWorkspaceConfig(projectDir);
  if (!file) {
    logErr("No skaile.yaml — run `skaile init` first");
    process.exit(1);
  }

  const resolved = path.resolve(filePath);
  if (!existsSync(resolved)) {
    logErr(`File not found: ${resolved}`);
    process.exit(1);
  }

  const { parsePresetYaml } = await import("@skaile/workspaces/library");
  const parseResult = parsePresetYaml(readFileSync(resolved, "utf-8"));
  if (!parseResult.ok || !parseResult.preset) {
    logErr("Preset validation failed:");
    for (const err of parseResult.errors) console.error(`  ${S.err("✗")} ${err}`);
    process.exit(1);
  }
  const preset = parseResult.preset;

  // Reject nested presets up front with a friendly message — v1 apply resolves
  // only the flat item set produced by `preset export`.
  if (preset.items.some((it) => it.ref.startsWith("preset:"))) {
    logErr(NESTED_PRESET_UNSUPPORTED);
    logInfo("Remove the nested `preset:` item(s) and re-run.");
    process.exit(1);
  }

  const values = parseInputs(opts.input);
  await promptMissingInputs(preset.items, preset.inputs, values, opts.yes === true);

  let items: WorkspaceItems;
  try {
    items = presetItemsToWorkspace(preset, values);
  } catch (err) {
    logErr((err as Error).message);
    process.exit(1);
  }

  const editor = WorkspaceYamlEditor.load(file.path);
  const summary = applyItems(editor, file.config, items);
  // Only the add paths mutate the doc — skip a no-op rewrite when nothing was added.
  const changed =
    summary.addedSources.length +
    summary.addedDeps.length +
    summary.addedConnectors.length +
    summary.addedMcp.length;
  if (changed > 0) editor.save();

  printApplySummary(summary, items.envVarsToSet);

  if (opts.deploy) {
    await runPresetInstall(projectDir);
  } else {
    logInfo("Skipped deploy (--no-deploy). Run `skaile install` to deploy the new dependencies.");
  }
}

/** Parse repeatable `KEY=VAL` strings into a value map, exiting on a bad pair. */
function parseInputs(pairs: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of pairs) {
    const eq = pair.indexOf("=");
    if (eq <= 0) {
      logErr(`Invalid --input "${pair}" — expected KEY=VALUE.`);
      process.exit(1);
    }
    out[pair.slice(0, eq)] = pair.slice(eq + 1);
  }
  return out;
}

/**
 * Interactively fill non-secret placeholders that have no `--input` value.
 * Secrets are never prompted — they resolve to `env:` references. A `--yes` run
 * or a non-TTY leaves gaps for {@link presetItemsToWorkspace} to fill from
 * defaults or reject as required.
 */
async function promptMissingInputs(
  presetItems: { placeholders?: Placeholder[] }[],
  presetInputs: Placeholder[] | undefined,
  values: Record<string, string>,
  yes: boolean,
): Promise<void> {
  const seen = new Set<string>();
  const toPrompt: Placeholder[] = [];
  const collect = (ph: Placeholder): void => {
    if (seen.has(ph.key) || isSecretPlaceholder(ph)) return;
    seen.add(ph.key);
    if (values[ph.key] === undefined) toPrompt.push(ph);
  };
  for (const input of presetInputs ?? []) collect(input);
  for (const item of presetItems) for (const ph of item.placeholders ?? []) collect(ph);

  if (toPrompt.length === 0 || yes || !process.stdin.isTTY) return;

  const rl = readline.createInterface({ input: process.stdin, output: process.stderr });
  try {
    for (const ph of toPrompt) {
      const def = getDefaultValue(ph);
      const suffix = def !== undefined ? ` [${String(def)}]` : "";
      const answer = (await rl.question(`${ph.prompt ?? ph.key}${suffix}: `)).trim();
      if (answer) values[ph.key] = answer;
      else if (def !== undefined) values[ph.key] = String(def);
    }
  } finally {
    rl.close();
  }
}

/** Per-section record of what the apply added vs skipped (on id collision). */
interface ApplySummary {
  addedDeps: string[];
  skippedDeps: string[];
  addedConnectors: string[];
  skippedConnectors: string[];
  unchangedConnectors: string[];
  addedMcp: string[];
  skippedMcp: string[];
  unchangedMcp: string[];
  addedSources: string[];
  skippedSources: Array<{ url: string; keptPin?: string; presetPin?: string }>;
  unchangedSources: string[];
}

/**
 * Additively merge resolved items into the editor. Deps dedupe; connectors and
 * mcp servers are skipped (not overwritten) when an entry with the same id
 * already exists AND differs — the user's own config always wins.
 */
function applyItems(
  editor: WorkspaceYamlEditor,
  config: SkWorkspaceConfig,
  items: WorkspaceItems,
): ApplySummary {
  const s: ApplySummary = {
    addedDeps: [],
    skippedDeps: [],
    addedConnectors: [],
    skippedConnectors: [],
    unchangedConnectors: [],
    addedMcp: [],
    skippedMcp: [],
    unchangedMcp: [],
    addedSources: [],
    skippedSources: [],
    unchangedSources: [],
  };

  // Register sources first (deduped by normalized url) so a later install can
  // clone them; an existing url with a different pin is left as-is (user wins).
  const existingSourceUrls = new Map(
    editor.getSources().map((src) => [normalizeRepoUrl(src.url), src]),
  );
  for (const src of items.sources) {
    const key = normalizeRepoUrl(src.url);
    const existing = existingSourceUrls.get(key);
    if (!existing) {
      editor.setSource(src);
      existingSourceUrls.set(key, src);
      s.addedSources.push(src.url);
      // `?? undefined` normalizes YAML's `null` (blank `~`) and undefined to one
      // value; a differing pin is skipped (existing wins), identical is a no-op.
    } else if ((existing.pin ?? undefined) !== (src.pin ?? undefined)) {
      s.skippedSources.push({ url: src.url, keptPin: existing.pin, presetPin: src.pin });
    } else {
      s.unchangedSources.push(src.url);
    }
  }

  for (const ref of items.dependencies) {
    if (editor.addDependency(ref)) s.addedDeps.push(ref);
    else s.skippedDeps.push(ref);
  }

  // An entry with an existing id is never overwritten: identical → unchanged,
  // different → skipped (the user's own config wins). Only a new id is written.
  const existingConnectors = new Map((config.connectors ?? []).map((c) => [c.id, c]));
  for (const conn of items.connectors) {
    const existing = existingConnectors.get(conn.id);
    if (existing) {
      (stableEqual(existing, conn) ? s.unchangedConnectors : s.skippedConnectors).push(conn.id);
      continue;
    }
    editor.setConnector(conn);
    s.addedConnectors.push(conn.id);
  }

  const existingMcp = new Map((config.mcp_servers ?? []).map((m) => [m.id, m]));
  for (const mcp of items.mcpServers) {
    const existing = existingMcp.get(mcp.id);
    if (existing) {
      (stableEqual(existing, mcp) ? s.unchangedMcp : s.skippedMcp).push(mcp.id);
      continue;
    }
    editor.setMcpServer(mcp);
    s.addedMcp.push(mcp.id);
  }

  return s;
}

/** Key-order-insensitive structural equality (for connector/mcp collision checks). */
function stableEqual(a: unknown, b: unknown): boolean {
  return canonicalJson(a) === canonicalJson(b);
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_k, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([x], [y]) => x.localeCompare(y)),
        )
      : v,
  );
}

/** Print what was added/skipped plus the env vars the user must set. */
function printApplySummary(summary: ApplySummary, envVarsToSet: string[]): void {
  const added =
    summary.addedSources.length +
    summary.addedDeps.length +
    summary.addedConnectors.length +
    summary.addedMcp.length;
  if (added === 0) {
    logInfo("Nothing to apply — everything was already present.");
  } else {
    if (summary.addedSources.length > 0) {
      logOk(`Registered ${summary.addedSources.length} source(s).`);
    }
    logOk(
      `Applied ${summary.addedDeps.length} dependency(ies), ` +
        `${summary.addedConnectors.length} connector(s), ${summary.addedMcp.length} mcp server(s).`,
    );
  }

  const skipped = [
    ...summary.skippedSources.map(
      (s) =>
        `source ${s.url} (kept pin '${s.keptPin ?? "none"}', preset had '${s.presetPin ?? "none"}')`,
    ),
    ...summary.skippedDeps.map((d) => `dependency ${d} (already present)`),
    ...summary.skippedConnectors.map((c) => `connector ${c} (id exists, differs)`),
    ...summary.skippedMcp.map((m) => `mcp ${m} (id exists, differs)`),
  ];
  for (const line of skipped) logWarn(`Skipped ${line}`);

  const unchanged =
    summary.unchangedConnectors.length +
    summary.unchangedMcp.length +
    summary.unchangedSources.length;
  if (unchanged > 0)
    logInfo(`${unchanged} source/connector/mcp entry(ies) already matched — left as-is.`);

  if (envVarsToSet.length > 0) {
    logWarn(
      `This preset references secrets by env var — provide them before running: ${envVarsToSet.join(", ")}`,
    );
    logInfo("Add them to .env (gitignored) or export in your shell, e.g. export <VAR>=…");
  }
}

/** Deploy newly added skill deps, then surface soft deps as `skaile install` does. */
async function runPresetInstall(projectDir: string): Promise<void> {
  try {
    const { AssetManager } = await import("@skaile/workspaces/asset-manager");
    const am = new AssetManager({ projectDir });
    logInfo("Installing new dependencies…");
    const result = await am.install({});
    for (const d of result.deployed) logOk(d);
    for (const m of result.missing) logErr(`Missing: ${m}`);
    // Report-only body-link soft deps, matching the `skaile install` surface.
    await reportInstalledSoftDeps(am);
  } catch (err) {
    logErr(`Install failed: ${err instanceof Error ? err.message : String(err)}`);
    logInfo("Run `skaile install` to deploy the new dependencies.");
  }
}

// ── preset validate implementation ───────────────────────────────────────────

/**
 * Read + parse the preset file, exiting the process on read/parse failure.
 * Returns the validated preset manifest on success.
 */
async function loadPresetForValidation(resolved: string): Promise<PresetManifest> {
  if (!existsSync(resolved)) {
    logErr(`File not found: ${resolved}`);
    process.exit(1);
  }

  const content = readFileSync(resolved, "utf-8");

  // Parse
  const { parsePresetYaml } = await import("@skaile/workspaces/library");

  const parseResult = parsePresetYaml(content);
  if (!parseResult.ok) {
    logErr("Preset validation failed (parse errors):");
    for (const err of parseResult.errors) {
      console.log(`  ${S.err("✗")} ${err}`);
    }
    process.exit(1);
  }

  // Note: ParseResult.preset is optional even when ok; on `ok: true` it's
  // always set by parsePreset(), but TypeScript can't infer that. Guard.
  if (!parseResult.preset) {
    logErr("Preset validation failed: parser returned no manifest");
    process.exit(1);
  }
  return parseResult.preset;
}

/**
 * Surface a count of nested preset refs. The full depth check runs at
 * apply-time via validateNestingDepth() inside applyPresetWithLibrary.
 */
async function reportNestedPresetRefs(preset: PresetManifest): Promise<void> {
  // Check structural nesting: detectNestedPresetRefs requires a known-refs
  // set, which we don't have at structural-validation time. The full
  // depth check runs at apply-time via validateNestingDepth(presetRef,
  // currentDepth) inside applyPresetWithLibrary. Surface a count only.
  const { detectNestedPresetRefs } = await import("@skaile/workspaces/library");
  const nestedRefs = detectNestedPresetRefs(preset);
  if (nestedRefs.length > 0) {
    logInfo(`Nested preset refs: ${nestedRefs.length} (depth validated at apply-time)`);
  }
}

/**
 * Detect cycles in the preset's item-ref graph, exiting the process on a cycle.
 */
async function checkPresetCycles(preset: PresetManifest, resolved: string): Promise<void> {
  // Check for cycle detection (requires resolving refs against library)
  // Phase 1: basic structural validation only. Full cycle detection
  // requires the discovery package's detectCycles() with a requires graph,
  // which needs the full Library context. We validate structure here.
  const { detectCycles } = await import("@skaile/workspaces/discovery");

  // Build a mini requires graph from the preset items
  const presetRef = path.basename(resolved, ".preset.yaml");
  const edges: Array<{ from: string; to: string; field: string }> = [];
  for (const item of preset.items) {
    if (item.ref) {
      edges.push({ from: presetRef, to: item.ref, field: "items[].ref" });
    }
  }

  if (edges.length === 0) return;

  const cycleResult = detectCycles(edges);
  if (!cycleResult.acyclic) {
    logErr("Cycle detected in preset references:");
    if (cycleResult.cyclePath) {
      console.log(`  ${S.err("✗")} ${cycleResult.cyclePath.join(" → ")}`);
    }
    process.exit(1);
  }
}

/**
 * Validate per-item placeholder structure (dry-run with no input values).
 * Returns true when any placeholder issues were surfaced as warnings.
 */
export async function checkPresetPlaceholders(allPlaceholders: Placeholder[]): Promise<boolean> {
  if (allPlaceholders.length === 0) return false;

  const { validatePlaceholder } = await import("@skaile/workspaces/library");
  const invalidEntries: Array<{ key: string; errors: string[] }> = [];
  for (const ph of allPlaceholders) {
    // Required-ness is enforced at apply, not here — a required-no-default secret
    // is exactly what `export --redact` emits. Only type-check a present default.
    if (ph.default === undefined) continue;
    const result = validatePlaceholder(ph, ph.default);
    if (!result.valid) {
      invalidEntries.push({ key: ph.key, errors: result.errors });
    }
  }
  if (invalidEntries.length === 0) return false;

  logWarn("Placeholder issues:");
  for (const r of invalidEntries) {
    console.log(`  ${S.warn("!")} ${r.key}: ${r.errors.join(", ")}`);
  }
  return true;
}

/** Print the validation summary table + final pass/warn line. */
function printPresetSummary(
  preset: PresetManifest,
  placeholderCount: number,
  hasWarnings: boolean,
): void {
  console.log();
  console.log(S.heading("  Preset Validation"));
  console.log(`  ${S.rule(40)}`);
  console.log(`  ${S.label("Name")}          ${preset.name}`);
  console.log(`  ${S.label("Version")}       ${preset.version ?? "unversioned"}`);
  console.log(`  ${S.label("Items")}         ${preset.items.length}`);
  console.log(`  ${S.label("Inputs")}         ${preset.inputs?.length ?? 0}`);
  console.log(`  ${S.label("Placeholders")}  ${placeholderCount}`);
  console.log();

  if (hasWarnings) {
    logWarn("Validation passed with warnings");
  } else {
    logOk("Preset is valid");
  }
}

/** Full `skaile preset validate <path>` pipeline. */
async function runPresetValidate(filePath: string): Promise<void> {
  const resolved = path.resolve(filePath);

  const preset = await loadPresetForValidation(resolved);

  await reportNestedPresetRefs(preset);
  await checkPresetCycles(preset, resolved);

  const allPlaceholders = preset.items.flatMap((item) => item.placeholders ?? []);
  const hasWarnings = await checkPresetPlaceholders(allPlaceholders);

  printPresetSummary(preset, allPlaceholders.length, hasWarnings);
}

// ── Template ────────────────────────────────────────────────────────────────

function generatePresetTemplate(name: string): string {
  return `# ${name}.preset.yaml
# Polymorphic Preset — composition + configuration primitive for the asset store.
# See: _devlog/specs/2026-05-03-ai-asset-store-v2-design.md § Presets

name: "${name}"
version: "1.0.0"
description: "TODO: describe what this preset configures"

# Items — each becomes a Library Instance when the preset is applied.
# Ref types:
#   - Asset ref (kind:publisher/name@pin) → creates new Instance
#   - Library Instance ref (#uuid) → reuses existing Instance
#   - Nested Preset ref (preset:path.preset.yaml) → recursive apply (max depth 3)
items:
  - id: example-item
    ref: "skill:@skaile/example-skill@^1.0"
    # Placeholders — values the user provides at apply-time, declared on the item
    # that uses them. Types: text, multiline, secret, number, boolean, enum, oauth,
    # instance-picker
    placeholders:
      - key: EXAMPLE_KEY
        type: text
        label: "Example placeholder"
        required: true
        # default: "some-value"
        # hint: "Help text shown to the user"
    config:
      # Use \${INPUT:EXAMPLE_KEY} for placeholder interpolation
      setting: "\${INPUT:EXAMPLE_KEY}"
    # credential_ref: "\${SECRET:EXAMPLE_KEY}"  # for secret placeholders

# Preset-level inputs shared across items (rare) — top-level \`inputs:\`, not
# \`placeholders:\` (that key lives on items). Same shape as a placeholder:
# inputs:
#   - key: SHARED_KEY
#     type: text
#     label: "Shared across all items"
`;
}
